import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Image as ImageIcon, Search, Plus, Store, Loader2, Trash2, Star } from "lucide-react";

const SLOT_HINT = {
  pannello: "Il cliente Free sceglie tra questi moduli. Il primo è il default.",
  inverter: "Il motore sceglie in automatico l'inverter adatto (potenza/fase/ibrido) tra questi.",
  accumulo: "Il motore sceglie in automatico l'accumulo adatto (regole HV/LV) tra questi.",
  ricarica: "Accessori mostrati come opzione; non entrano nel dimensionamento automatico.",
};

export function VetrinaSlots() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);
  const [listino, setListino] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/vetrina").then((r) => setSlots(r.data.slots || [])).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openPicker = (s) => {
    setPicker(s); setQ("");
    if (!loaded) api.get("/listino").then((r) => { setListino(r.data.items || []); setLoaded(true); }).catch((e) => toast.error(formatApiError(e)));
  };

  const alreadyIn = useMemo(() => new Set((picker?.codici) || []), [picker]);
  const options = useMemo(() => {
    if (!picker) return [];
    const s = q.trim().toLowerCase();
    return listino.filter((it) => it.tipologia === picker.tipologia && !alreadyIn.has(it.codice)
      && (!s || [it.codice, it.descrizione, it.marca].some((v) => String(v || "").toLowerCase().includes(s))));
  }, [listino, picker, q, alreadyIn]);

  const mutate = async (payload, ok) => {
    setBusy(true);
    try { const { data } = await api.put("/vetrina", payload); setSlots(data.slots || []); if (ok) toast.success(ok); return data.slots; }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const add = async (codice) => { await mutate({ slot: picker.slot, action: "add", codice }, "Prodotto aggiunto"); setPicker(null); };
  const remove = (slot, codice) => mutate({ slot, action: "remove", codice }, "Prodotto rimosso");
  const setDefault = (slot, codice) => mutate({ slot, action: "set_default", codice }, "Default aggiornato");

  const uploadMedia = async (level, key, files) => {
    if (!files?.[0]) return;
    const fd = new FormData(); fd.append("file", files[0]);
    setBusy(true);
    try { const { data } = await api.post(`/vetrina/media/${level}/${encodeURIComponent(key)}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setSlots(data.slots || []); toast.success("Foto caricata"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const delMedia = async (level, key) => {
    setBusy(true);
    try { const { data } = await api.delete(`/vetrina/media/${level}/${encodeURIComponent(key)}/image`); setSlots(data.slots || []); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const setLink = async (level, key, current) => {
    const url = window.prompt("Link scheda tecnica (URL). Lascia vuoto per rimuovere.", current || "");
    if (url === null) return;
    setBusy(true);
    try { const { data } = await api.put(`/vetrina/media/${level}/${encodeURIComponent(key)}`, { datasheet_url: url }); setSlots(data.slots || []); toast.success("Link scheda aggiornato"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 rounded-xl border-primary/30 bg-gradient-to-br from-primary/[0.05] to-transparent mb-2" data-testid="vetrina-section">
      <div className="flex items-center gap-2 mb-1">
        <Store className="h-5 w-5 text-primary" strokeWidth={1.5} />
        <h1 className="text-2xl font-bold">Vetrina</h1>
      </div>
      <p className="text-sm text-foreground mb-1 font-medium">👁️ La <b>Vetrina</b> è ciò che il pubblico vede nell'<b>Area Free</b>: è la sola gamma prodotti mostrata nel configuratore pubblico.</p>
      <p className="text-sm text-muted-foreground mb-4">I prodotti selezionati qui sono l'<b>unico catalogo</b> utilizzabile dal configuratore pubblico (Area Free). Tutti i dati (nome, descrizione, foto, specifiche) arrivano dal <b>Listino ufficiale</b>. Al cliente <b>non viene mai mostrato il prezzo</b>.</p>
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
      ) : (
        <div className="space-y-5">
          {slots.map((s) => (
            <div key={s.slot} data-testid={`vetrina-slot-${s.slot}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">{s.label} <span className="text-xs font-normal text-muted-foreground">· {(s.items || []).length} prodotti</span></div>
                  <div className="text-[11px] text-muted-foreground">{SLOT_HINT[s.slot]}</div>
                </div>
                <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => openPicker(s)} data-testid={`vetrina-add-${s.slot}`}><Plus className="h-4 w-4" /> Aggiungi</Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2 text-[11px]">
                <span className="text-muted-foreground uppercase tracking-wide text-[10px]">Media categoria (fallback):</span>
                <input id={`catimg-${s.slot}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia("categoria", s.slot, e.target.files)} data-testid={`vetrina-cat-img-${s.slot}`} />
                <button className="text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => document.getElementById(`catimg-${s.slot}`).click()}>{s.media_cat?.has_image ? "Cambia foto" : "Foto categoria"}</button>
                {s.media_cat?.has_image && <button className="text-destructive hover:underline disabled:opacity-50" disabled={busy} onClick={() => delMedia("categoria", s.slot)}>rimuovi</button>}
                <span className="text-muted-foreground">·</span>
                <button className="text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => setLink("categoria", s.slot, s.media_cat?.datasheet_url)} data-testid={`vetrina-cat-link-${s.slot}`}>{s.media_cat?.datasheet_url ? "Scheda categoria ✓" : "Link scheda categoria"}</button>
              </div>
              {(s.items || []).length === 0 ? (
                <div className="rounded-lg border border-dashed border-border text-xs text-muted-foreground py-5 text-center">Nessun prodotto — aggiungi dal listino</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {s.items.map((it, idx) => {
                    const isDefault = s.slot === "pannello" && idx === 0;
                    return (
                      <div key={it.codice} className="rounded-lg border border-border bg-background/60 p-2.5 flex gap-2.5" data-testid={`vetrina-item-${it.codice}`}>
                        <div className="w-14 h-14 rounded overflow-hidden border border-border bg-secondary/30 shrink-0 flex items-center justify-center">
                          {it.immagine_url ? <img src={it.immagine_url} alt="" className="w-full h-full object-contain bg-white" /> : <ImageIcon className="h-5 w-5 text-muted-foreground/40" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {isDefault && <span className="text-[9px] uppercase font-bold text-primary bg-primary/10 rounded px-1 py-0.5 flex items-center gap-0.5"><Star className="h-2.5 w-2.5" />Default</span>}
                            <div className="text-xs font-mono font-semibold truncate" title={it.codice}>{it.codice}</div>
                          </div>
                          <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{it.descrizione}</div>
                          <div className="flex flex-wrap gap-2 mt-1.5 items-center">
                            {s.slot === "pannello" && !isDefault && (
                              <button className="text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => setDefault(s.slot, it.codice)} data-testid={`vetrina-default-${it.codice}`}>Imposta default</button>
                            )}
                            <input id={`prodimg-${it.codice}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia("prodotto", it.codice, e.target.files)} data-testid={`vetrina-prod-img-${it.codice}`} />
                            <button className="text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => document.getElementById(`prodimg-${it.codice}`).click()}>{it.media_prod?.has_image ? "Foto ✓" : "Foto"}</button>
                            {it.media_prod?.has_image && <button className="text-[11px] text-muted-foreground hover:underline disabled:opacity-50" disabled={busy} onClick={() => delMedia("prodotto", it.codice)}>×</button>}
                            <button className="text-[11px] text-primary hover:underline disabled:opacity-50" disabled={busy} onClick={() => setLink("prodotto", it.codice, it.media_prod?.datasheet_url)} data-testid={`vetrina-prod-link-${it.codice}`}>{it.media_prod?.datasheet_url ? "Scheda ✓" : "Scheda"}</button>
                            <button className="text-[11px] text-destructive hover:underline inline-flex items-center gap-0.5 disabled:opacity-50" disabled={busy} onClick={() => remove(s.slot, it.codice)} data-testid={`vetrina-remove-${it.codice}`}><Trash2 className="h-3 w-3" /> Rimuovi</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Aggiungi {picker?.label} dal listino · {picker?.tipologia}</DialogTitle></DialogHeader>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-9" data-testid="vetrina-picker-search" autoFocus />
          </div>
          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
            {options.length === 0 ? <div className="text-sm text-muted-foreground py-8 text-center">Nessun articolo disponibile.</div>
             : options.map((it) => (
              <button key={it.codice} onClick={() => add(it.codice)} disabled={busy} className="w-full flex items-center gap-3 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/40 p-2.5 text-left transition-colors disabled:opacity-50" data-testid={`vetrina-option-${it.codice}`}>
                <div className="w-12 h-12 rounded overflow-hidden border border-border bg-secondary/30 shrink-0 flex items-center justify-center">{it.immagine_url ? <img src={it.immagine_url} alt="" className="w-full h-full object-contain bg-white" /> : <ImageIcon className="h-5 w-5 text-muted-foreground/40" />}</div>
                <div className="min-w-0 flex-1"><div className="text-xs font-mono font-semibold truncate">{it.codice}</div><div className="text-xs text-muted-foreground line-clamp-1">{it.descrizione}</div></div>
                <Plus className="h-4 w-4 text-primary shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
